import { api } from './api';
import legal from '../config/legal.json';
import { 
  ILoginCredentials, 
  IRegisterCredentials,
  IAuthResult,
  SocialAuthResult,
  SocialAuthCompletion,
  IAppleLoginCredentials,
  IPasswordResetRequest,
  IPasswordResetConfirmation,
  IAuthToken,
  AuthError
} from '../types/auth';
import { IUser } from '../types/user';

export const authApi = {
  /**
   * Login with credentials
   */
  async login(credentials: ILoginCredentials): Promise<IAuthResult> {
    try {
      const response = await api.post<IAuthResult>('/auth/login', credentials);
      return response.data; // API wrapper returns { success, message, data }, so access data property
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Register new user
   */
  async register(credentials: IRegisterCredentials): Promise<IAuthResult> {
    try {
      const response = await api.post<IAuthResult>('/auth/register', {
        ...credentials, termsAccepted: credentials.acceptTerms === true,
        ageConfirmed: credentials.ageConfirmed === true, termsVersion: legal.version
      });
      return response.data; // API wrapper returns { success, message, data }, so access data property
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Get current user data
   */
  async getCurrentUser(): Promise<IUser> {
    try {
      const response = await api.get<{ data: IUser }>('/users/me');
      
      // The API service returns the server response: { success, message, data }
      // The server's data structure is: { success, data: user }
      if (response && typeof response === 'object' && 'data' in response) {
        const user = (response as any).data;
        
        if (!user) {
          throw new Error('User data not found in response');
        }
        
        return user;
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Update user profile
   */
  async updateUser(userData: Partial<IUser>): Promise<IUser> {
    try {
      const response = await api.patch<{ user: IUser }>('/users/me', userData);
      return (response as any).data.user;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(data: IPasswordResetRequest): Promise<void> {
    try {
      await api.post('/auth/forgot-password', data);
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Confirm password reset
   */
  async confirmPasswordReset(data: IPasswordResetConfirmation): Promise<void> {
    try {
      // Matches the real route: PUT /auth/reset-password/:token.
      await api.put(`/auth/reset-password/${encodeURIComponent(data.token)}`, {
        password: data.password,
        confirmPassword: data.confirmPassword,
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Change password when logged in (or set one for the first time on an
   * OAuth-only account -- omit `currentPassword` in that case). This route
   * previously didn't exist server-side (see password.controller.ts); the
   * request body key was also updated from `newPassword` to `password` to
   * match the now-real endpoint's contract.
   */
  async changePassword(newPassword: string, currentPassword?: string): Promise<void> {
    try {
      await api.post('/auth/password/change', {
        currentPassword,
        password: newPassword
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Refresh access token
   *
   * Delegates to `api.refreshAccessToken()`, which deduplicates concurrent
   * refresh attempts across the whole app (this call site, plus the axios
   * response interceptor's own reactive 401-retry). Calling the endpoint
   * directly here would let those two race to use the same single-use
   * refresh token, which is what caused spurious logouts — most visibly
   * right after resuming a backgrounded iOS Safari tab.
   */
  async refreshToken(): Promise<IAuthToken> {
    try {
      const result = await api.refreshAccessToken();
      return {
        token: result.token,
        refreshToken: result.refreshToken || '',
        expiresAt: Date.now() + result.expiresIn * 1000
      };
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<void> {
    try {
      await api.post('/auth/email/verify', { token });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Login or register with Apple
   */
  async appleLogin(credentials: IAppleLoginCredentials): Promise<SocialAuthResult> {
    try {
      const response = await api.post<SocialAuthResult>('/auth/apple', credentials);
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /** Login or register with Google OAuth */
  async googleLogin(credential: string): Promise<SocialAuthResult> {
    try {
      const response = await api.post<SocialAuthResult>('/auth/google', { credential });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  async completeSocialLogin(payload: SocialAuthCompletion): Promise<IAuthResult> {
    try {
      const response = await api.post<IAuthResult>('/auth/social/complete', payload);
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Request email verification resend
   */
  async resendVerificationEmail(): Promise<string> {
    try {
      const response = await api.post('/auth/email/verify/resend');
      return response.message || 'A new verification email has been sent. The link is valid for 24 hours.';
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Check if username is available
   */
  async checkUsernameAvailability(username: string): Promise<boolean> {
    try {
      const response = await api.get<{ available: boolean }>(`/auth/check-username/${username}`);
      return (response as any).data.available;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Permanently delete the current user's account and personal data.
   * Reuses the same `DELETE /auth/account` endpoint the mobile app calls —
   * all deletion/anonymization logic lives server-side (see
   * accountDeletion.service.ts), so this is just a thin passthrough.
   * `password` is required for local-auth accounts; omit it for pure
   * Google/Apple accounts and pass `confirm: true` instead.
   */
  async deleteAccount(payload: { password?: string; confirm?: boolean; expectedUserId?: string } = {}): Promise<void> {
    try {
      await api.delete('/auth/account', { data: payload });
    } catch (error) {
      if ((error as any)?.status === 409) throw new AuthError(
        'The signed-in account changed. Reopen account deletion and confirm again.', 'ACCOUNT_CHANGED', undefined, 409);
      throw this.handleAuthError(error);
    }
  },

  /**
   * Handle authentication errors
   */
  handleAuthError(error: unknown): AuthError {
    if (error instanceof Error) {
      // Map specific error codes to user-friendly messages
      const errorMessage = this.getErrorMessage(error);
      const errorCode = (error as any)?.code ||
        (error.name === 'ValidationError' ? 'VALIDATION_ERROR' : 'AUTH_ERROR');
      const status = (error as any)?.status;
      const validationErrors = (error as any)?.validationErrors;
      
      return new AuthError(errorMessage, errorCode, undefined, status, validationErrors);
    }
    return new AuthError('An unexpected authentication error occurred');
  },

  /**
   * Get user-friendly error message
   */
  getErrorMessage(error: Error & { status?: number }): string {
    switch (error.status) {
      case 401:
        return 'Invalid credentials';
      case 403:
        return 'Access denied';
      case 404:
        return 'User not found';
      case 409:
        return error.message || 'This sign-in method is already connected to an account';
      case 422:
        return 'Invalid input data';
      case 429:
        return 'Too many attempts. Please try again later';
      default:
        return error.message || 'Authentication failed';
    }
  }
};
